import { Card, Col, Row, Typography, Button, List, Badge, Tooltip, Grid, Tag } from 'antd';
import { mqtt, iot } from 'aws-iot-device-sdk-v2';
import React, { useContext, useEffect, useState } from 'react';
import { SimulatorContext } from 'store/context';
import { FaCarOn, FaCar } from 'react-icons/fa6';
import { BsLightbulb, BsLightbulbFill } from 'react-icons/bs';
import { TiWeatherPartlySunny } from 'react-icons/ti';
import { FaLocationCrosshairs } from 'react-icons/fa6';
import { FaRegWindowMinimize } from 'react-icons/fa';
import { FaWindowMaximize } from 'react-icons/fa6';
import { IoMdLock, IoMdUnlock } from 'react-icons/io';
import { TbAirConditioning, TbAirConditioningDisabled } from 'react-icons/tb';
import { PiSeatbeltBold } from 'react-icons/pi';
import { WifiOutlined } from '@ant-design/icons';
import _ from 'lodash';
import { createPortal } from 'react-dom';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const IndicatorChip = ({ label, icon, active, unknown, color = '#10b981', statusLabel }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 5,
      padding: '10px 6px',
      borderRadius: 10,
      background: unknown ? '#f8fafc' : active ? `${color}12` : '#f8fafc',
      border: `1px solid ${unknown ? '#e2e8f0' : active ? `${color}44` : '#e2e8f0'}`,
      transition: 'all 0.2s',
      minWidth: 0,
    }}
  >
    <span
      style={{
        fontSize: 9,
        fontWeight: 600,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        textAlign: 'center',
        lineHeight: 1.3,
        wordBreak: 'break-word',
      }}
    >
      {label}
    </span>
    <div style={{ fontSize: 26, lineHeight: 1, color: unknown ? '#cbd5e1' : active ? color : '#94a3b8' }}>
      {icon}
    </div>
    <span style={{ fontSize: 9, fontWeight: 700, color: unknown ? '#cbd5e1' : active ? color : '#94a3b8' }}>
      {unknown ? '—' : (statusLabel || (active ? 'ON' : 'OFF'))}
    </span>
  </div>
);


const SectionCard = ({ title, children }) => (
  <Card
    style={{ borderRadius: 12, border: '1px solid #e2e8f0', height: '100%' }}
    styles={{ body: { padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 } }}
  >
    <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {title}
    </span>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8 }}>
      {children}
    </div>
  </Card>
);


const EmptyState = ({ onFetch }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      padding: '48px 24px',
      borderRadius: 12,
      background: '#f8fafc',
      border: '1px dashed #cbd5e1',
    }}
  >
    <WifiOutlined style={{ fontSize: 36, color: '#cbd5e1' }} />
    <Text style={{ color: '#94a3b8', fontSize: 13 }}>No shadow state loaded yet</Text>
    <Button type="primary" onClick={onFetch}>
      Fetch Initial State
    </Button>
  </div>
);


const RemoteFunctions = ({ onLiveTrackingShadowUpdate, portalControls }) => {
  const {
    stsToken: credentials,
    vehicleSimulatorSettings: { serialNo },
  } = useContext(SimulatorContext);

  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [settleDown, setSettleDown] = useState(true);
  const [initState, setInitState] = useState({});
  const [icMessage, setIcMessage] = useState([]);
  const [logMessage, setLogMessage] = useState('');
  const [desired, setDesired] = useState({});
  const [listenInitial, setListenInitial] = useState(false);
  const [listenRemote, setListenRemote] = useState(false);
  const [ignite, setIgnite] = useState(false);

  async function connect_websocket(type) {
    return await new Promise((resolve, reject) => {
      let config = iot.AwsIotMqttConnectionConfigBuilder.new_builder_for_websocket()
        .with_clean_session(true)
        .with_client_id('test-' + Math.floor(Math.random() * 100000000))
        .with_endpoint('a3p0q0u1ruwh8a-ats.iot.us-east-2.amazonaws.com')
        .with_credentials('us-east-2', credentials.accessKeyId, credentials.secretAccessKey, credentials.sessionToken)
        .with_keep_alive_seconds(30)
        .build();
      setLogMessage(`Connecting websocket... for ${type}`);
      const client = new mqtt.MqttClient();
      const connection = client.new_connection(config);
      connection.on('connect', () => {
        resolve(connection);
        if (type === 'init') setListenInitial(true);
        else if (type === 'remote') setListenRemote(true);
      });
      connection.on('interrupt', () => {
        setLogMessage('Connection interrupted');
        if (type === 'init') setListenInitial(false);
        else if (type === 'remote') setListenRemote(false);
      });
      connection.on('resume', (rc, sp) => {
        setLogMessage(`Resumed: rc: ${rc} existing session: ${sp}`);
        if (type === 'init') setListenInitial(true);
        else if (type === 'remote') setListenRemote(true);
      });
      connection.on('disconnect', () => {
        setLogMessage('Disconnected');
        if (type === 'init') setListenInitial(false);
        else if (type === 'remote') setListenRemote(false);
      });
      connection.on('error', (error) => {
        reject(error);
        if (type === 'init') setListenInitial(false);
        else if (type === 'remote') setListenRemote(false);
      });
      connection.connect();
    });
  }

  const populateIncomingMessages = (remoteFunctions = {}) => {
    const keys = Object.keys(remoteFunctions);
    if (keys.includes('climateControl')) {
      setIcMessage((m) => [`Climate Control is turned ${remoteFunctions?.climateControl?.action}`, ...m]);
    } else if (keys.includes('locateMyVehicle')) {
      setIcMessage((m) => ['Locate my vehicle is turned ON', ...m]);
    } else if (keys.includes('lampControl')) {
      setIcMessage((m) => [`Lamp Control is turned ${remoteFunctions?.lampControl?.action}`, ...m]);
    } else if (keys.includes('ignition')) {
      setIcMessage((m) => [`Vehicle Ignition is turned ${remoteFunctions?.ignition?.action}`, ...m]);
    } else if (keys.includes('windowControl')) {
      const [window] = Object.keys(remoteFunctions?.windowControl);
      setIcMessage((m) => [`Vehicle ${window} window is ${remoteFunctions?.windowControl?.[window]}`, ...m]);
    } else if (keys.includes('doorControl')) {
      const [door] = Object.keys(remoteFunctions?.doorControl);
      setIcMessage((m) => [`Vehicle ${door} door is ${remoteFunctions?.doorControl?.[door]}`, ...m]);
    } else if (keys.includes('liveTrackingSession')) {
      const { sessionId } = remoteFunctions?.liveTrackingSession || {};
      onLiveTrackingShadowUpdate?.(remoteFunctions?.liveTrackingSession);
      setIcMessage((m) => [`Vehicle live tracking session is ${sessionId ? 'started' : 'stopped'}`, ...m]);
    }
    setInitState((s) => _.merge({}, s, remoteFunctions));
  };

  const init = () => {
    connect_websocket('remote')
      .then((connection) => {
        connection
          .subscribe(`$aws/things/${serialNo}/shadow/update/accepted`, mqtt.QoS.AtLeastOnce, (topic, payload) => {
            const message = new TextDecoder('utf8').decode(new Uint8Array(payload));
            setLogMessage(`Message received: topic=${topic} message=${message}`);
            const data = JSON.parse(message);
            const { state: { desired: { remoteFunctions } = {} } = {} } = data || {};
            setDesired(remoteFunctions);
          })
          .then((subscription) => {
            setLogMessage('start publish');
            connection.publish(subscription.topic, '{}', subscription.qos);
          });
      })
      .catch((reason) => setLogMessage(`Error while connecting: ${reason}`));
  };

  const getInitialState = () => {
    connect_websocket('init')
      .then((connection) => {
        connection
          .subscribe(`$aws/things/${serialNo}/shadow/get/accepted`, mqtt.QoS.AtLeastOnce, (topic, payload) => {
            const message = new TextDecoder('utf8').decode(new Uint8Array(payload));
            setLogMessage(`Message received: topic=${topic} message=${message}`);
            const data = JSON.parse(message);
            const { state: { desired: { remoteFunctions } = {} } = {} } = data || {};
            setInitState(remoteFunctions);
            setIgnite(true);
            connection.disconnect();
            setLogMessage('connection disconnected -> initial state');
          })
          .then((subscription) => {
            setLogMessage('start publish');
            connection.publish(`$aws/things/${serialNo}/shadow/get`, '{}', subscription.qos);
          });
      })
      .catch((reason) => setLogMessage(`Error while connecting: ${reason}`));
  };

  useEffect(() => { populateIncomingMessages(desired); }, [desired]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setTimeout(() => setSettleDown(false), 2000); }, []);

  const {
    ignition: { action: ignitionAction = '' } = {},
    climateControl: { action: climateAction = '' } = {},
    windowControl = {},
    doorControl = {},
    lampControl: { action: lampAction = '' } = {},
    locateMyVehicle: { action: locationAction = '' } = {},
    airCondition: { action: airConditionAction = '' } = {},
    seatBeltStatus = {},
  } = initState || {};

  const mqttControls = (
    <>
      <Button onClick={getInitialState} type="primary">Fetch Initial State</Button>
      <Button onClick={init} type="primary" disabled={!ignite} style={{ marginLeft: 8 }}>Connect</Button>
    </>
  );

  return (
    <>
      {/* page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Remote Functions</Title>
          {serialNo && <Text style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{serialNo}</Text>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {mqttControls}
        </div>
      </div>

      {portalControls && createPortal(mqttControls, document.getElementById('remote-functions-control-portal'))}

      <Row gutter={[16, 16]}>

        {/* ── indicator panel ── */}
        <Col xs={24}>
          {!ignite ? (
            <EmptyState onFetch={getInitialState} />
          ) : (
            <Row gutter={[12, 12]}>

              {/* Windows */}
              <Col xs={24} sm={12} lg={6}>
                <SectionCard title="Windows">
                  {[
                    { key: 'FRONT_LEFT', label: 'Front Left' },
                    { key: 'FRONT_RIGHT', label: 'Front Right' },
                    { key: 'REAR_LEFT', label: 'Rear Left' },
                    { key: 'REAR_RIGHT', label: 'Rear Right' },
                  ].map(({ key, label }) => {
                    const val = windowControl?.[key];
                    const isUp = val === 'UP';
                    return (
                      <IndicatorChip
                        key={key}
                        label={label}
                        icon={isUp ? <FaWindowMaximize /> : <FaRegWindowMinimize />}
                        active={isUp}
                        unknown={!val}
                        color="#2563eb"
                        statusLabel={val || '—'}
                      />
                    );
                  })}
                </SectionCard>
              </Col>

              {/* Doors */}
              <Col xs={24} sm={12} lg={6}>
                <SectionCard title="Door Locks">
                  {[
                    { key: 'FRONT_LEFT', label: 'Front Left' },
                    { key: 'FRONT_RIGHT', label: 'Front Right' },
                    { key: 'REAR_LEFT', label: 'Rear Left' },
                    { key: 'REAR_RIGHT', label: 'Rear Right' },
                  ].map(({ key, label }) => {
                    const val = doorControl?.[key];
                    const isLocked = val === 'LOCK';
                    return (
                      <IndicatorChip
                        key={key}
                        label={label}
                        icon={isLocked ? <IoMdLock /> : <IoMdUnlock />}
                        active={isLocked}
                        unknown={!val}
                        color="#10b981"
                        statusLabel={val || '—'}
                      />
                    );
                  })}
                </SectionCard>
              </Col>

              {/* Seat Belts */}
              <Col xs={24} sm={12} lg={6}>
                <SectionCard title="Seat Belts">
                  {[
                    { key: 'FRONT_LEFT', label: 'Front L' },
                    { key: 'FRONT_RIGHT', label: 'Front R' },
                    { key: 'REAR_MIDDLE', label: 'Rear Mid' },
                    { key: 'REAR_LEFT', label: 'Rear L' },
                    { key: 'REAR_RIGHT', label: 'Rear R' },
                  ].map(({ key, label }) => {
                    const val = seatBeltStatus?.[key];
                    const fastened = val === 'FASTENED';
                    return (
                      <IndicatorChip
                        key={key}
                        label={label}
                        icon={<PiSeatbeltBold />}
                        active={fastened}
                        unknown={!val}
                        color="#10b981"
                        statusLabel={fastened ? 'FASTENED' : val === 'UNFASTENED' ? 'OPEN' : '—'}
                      />
                    );
                  })}
                </SectionCard>
              </Col>

              {/* Controls */}
              <Col xs={24} sm={12} lg={6}>
                <SectionCard title="Controls">
                  <IndicatorChip
                    label="Ignition"
                    icon={ignitionAction === 'ON' ? <FaCarOn /> : <FaCar />}
                    active={ignitionAction === 'ON'}
                    unknown={!ignitionAction}
                    color="#059669"
                  />
                  <IndicatorChip
                    label="Climate"
                    icon={<TiWeatherPartlySunny />}
                    active={climateAction === 'ON'}
                    unknown={!climateAction}
                    color="#0891b2"
                  />
                  <IndicatorChip
                    label="Head Lamp"
                    icon={lampAction === 'ON' ? <BsLightbulbFill /> : <BsLightbulb />}
                    active={lampAction === 'ON'}
                    unknown={!lampAction}
                    color="#ca8a04"
                  />
                  <IndicatorChip
                    label="Location"
                    icon={<FaLocationCrosshairs />}
                    active={locationAction === 'ON'}
                    unknown={!locationAction}
                    color="#7c3aed"
                  />
                  <IndicatorChip
                    label="Air Con"
                    icon={airConditionAction === 'ON' ? <TbAirConditioning /> : <TbAirConditioningDisabled />}
                    active={airConditionAction === 'ON'}
                    unknown={!airConditionAction}
                    color="#2563eb"
                  />
                </SectionCard>
              </Col>

            </Row>
          )}
        </Col>

        {/* ── signal controls ── */}
        <Col xs={24} md={12}>
          <Card
            title="Signal Controls"
            loading={settleDown}
            style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <Text style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Initial Shadow Topic
                </Text>
                <div style={{ marginTop: 6 }}>
                  <Tooltip title={listenInitial ? 'MQTT connected' : 'MQTT not connected'} placement="right" color="#1e293b">
                    <Badge dot color={listenInitial ? 'green' : 'red'}>
                      <Text code style={{ fontSize: 11 }}>{`$aws/things/${serialNo}/shadow/get/accepted`}</Text>
                    </Badge>
                  </Tooltip>
                </div>
              </div>
              <div>
                <Text style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Incoming Remote Signals
                </Text>
                <div style={{ marginTop: 6 }}>
                  <Tooltip title={listenRemote ? 'MQTT connected' : 'MQTT not connected'} placement="right" color="#1e293b">
                    <Badge dot color={listenRemote ? 'green' : 'red'}>
                      <Text code style={{ fontSize: 11 }}>{`$aws/things/${serialNo}/shadow/update/accepted`}</Text>
                    </Badge>
                  </Tooltip>
                </div>
              </div>
              {logMessage && (
                <div>
                  <Text style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Remote Log
                  </Text>
                  <div style={{ marginTop: 6, padding: '8px 10px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>{logMessage}</Text>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* ── incoming signals ── */}
        <Col xs={24} md={12}>
          <Card
            title="Incoming Signals"
            loading={settleDown}
            style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
            extra={
              icMessage.length > 0 && (
                <Tag color="blue" style={{ borderRadius: 20, fontSize: 10 }}>{icMessage.length}</Tag>
              )
            }
          >
            {icMessage.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>
                No signals received yet
              </div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {icMessage.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 6,
                      marginBottom: 6,
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      fontSize: 12,
                      color: '#475569',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#2563eb',
                        flexShrink: 0,
                        marginTop: 5,
                        opacity: i === 0 ? 1 : 0.4,
                      }}
                    />
                    {msg}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

      </Row>
    </>
  );
};

export default RemoteFunctions;
